import type { MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { api } from "./api";

/**
 * What a "view" is, in one place.
 *
 * A view is one PLAYTHROUGH of a clip on a real player surface — a screen's regions, the
 * Goon Fuel widget, the Browse clip player — whether or not anyone is signed in and
 * whether or not a goon session is running. On a screen it starts counting once the clip
 * has really played on the glass (RegionPlayer's syncWatch); a page a scroll has loaded
 * ahead is not a view. Play it twice and that is two views. A clip
 * pinned with the loop button and left repeating all night is a view per repeat, because
 * that is what the number is being asked: how much has this clip actually been watched.
 *
 * THIS USED TO DEDUP FOR THE LIFETIME OF THE PAGE, and that is the bug it was reported as.
 * A TV left on /screen for a week counted each clip once, ever, so a wall running all
 * night and a tab someone opened and closed were the same number. On prod that put the
 * median clip at 13 views and a quarter of the catalog at six or fewer — numbers that
 * looked broken because they described page-loads, not watching.
 *
 * Two mechanisms here, and they answer different questions. Do not merge them:
 *
 *   THE WINDOW is about correctness. It swallows a duplicate event from a single
 *   playthrough (a player that reports `playable` twice, an `ended` that races a reveal)
 *   and nothing longer. Far below any real clip length — the shortest rung in the catalog
 *   is `clip_length:micro` and still runs seconds — so a looping clip is never silently
 *   collapsed by it.
 *
 *   THE BUFFER is about traffic. Counting per playthrough turned a nine-region wall on
 *   20s clips from nine requests ONCE into ~27 a minute, forever, per screen. Batching
 *   makes that six a minute whatever the region count. It carries a COUNT per clip rather
 *   than a set, because a clip that loops twice inside one flush window is two views and
 *   collapsing it would re-introduce the bug above by the back door.
 *
 * Previews must NOT call this — a tile's hover loop, the ambient backdrop, an editor's
 * source preview are not someone watching a clip.
 *
 * The goon session keeps its own per-play tally (api.session.view, behind the session gate
 * in DisplayApp); that is a different number and is not deduped here.
 */
const DEDUP_MS = 750;
const FLUSH_MS = 10_000;

/** clipKey -> when we last counted it. Trimmed on write so it cannot grow unbounded on a
 *  screen that runs for days. */
const lastSeen = new Map<string, number>();

/** clipKey -> the clip and how many playthroughs are waiting to be reported. */
const pending = new Map<string, { clip: MediaItem; n: number }>();
let timer: ReturnType<typeof setTimeout> | undefined;

/**
 * Send what has accumulated. Fire-and-forget: a lost request is lost views, not a retry
 * storm — the buffer is cleared BEFORE the request so a failure cannot double-count on the
 * next flush either.
 *
 * `keepalive` so the last window survives the tab closing. Note this is additive and
 * idempotent-ish, which is why a beacon is right here and wrong in DeviceScreen: that one
 * is a teardown DELETE that cannot tell a reload from a close, whereas a reload's views
 * genuinely happened and should land.
 */
function flush(keepalive = false): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (!pending.size) return;
  const clips = [...pending.values()];
  pending.clear();
  void api.clipViews.recordMany(clips, keepalive).catch(() => undefined);
}

if (typeof window !== "undefined") {
  // pagehide, deliberately NOT visibilitychange: a wall TV reports hidden the moment its
  // screensaver kicks in, and that wall is still playing. Same call useRadio makes.
  window.addEventListener("pagehide", () => flush(true));
}

export function reportClipView(item: MediaItem): void {
  // A vault upload has no public view count to keep — the anonymous clip-views route
  // resolves its token to nothing, by design, so the only thing this request could do is
  // 400. Its origin rides the wire as `sourceKind: "upload"`; the `upload:` prefix lives
  // on the internal id, which a served item never carries.
  if (item.sourceKind === "upload") return;
  const key = clipKey(item);
  const now = Date.now();
  const prev = lastSeen.get(key);
  if (prev !== undefined && now - prev < DEDUP_MS) return;
  lastSeen.set(key, now);
  if (lastSeen.size > 512) {
    for (const [k, t] of lastSeen) if (now - t > DEDUP_MS) lastSeen.delete(k);
  }

  const entry = pending.get(key);
  if (entry) entry.n += 1;
  else pending.set(key, { clip: item, n: 1 });
  // Leading edge stays un-set on purpose: the timer starts with the first view of a window
  // and is not extended by later ones, so a busy wall flushes on a fixed cadence rather
  // than never (which is what debouncing on every report would do).
  timer ??= setTimeout(() => {
    timer = undefined;
    flush();
  }, FLUSH_MS);
}
